/**
 * The expected gateway, transfer and payout fees — `/v1/admin/recon/gateway-fees` (R172).
 *
 * **This is the written quote, not a ledger.** The reconciliation passes compare what a provider
 * actually deducted against it, so a wrong line raises a break somebody reads, never a wrong
 * entry. That is why one finance admin adds a line with a reason and no second approver.
 *
 * **Versioned like the tax rates, and the server does the closing.** A new line for a schedule
 * that is still open closes its predecessor the day before; an overlapping window is refused
 * 409 `fee_window_overlap`. The console keeps no copy of either rule and shows the refusal as sent.
 *
 * **Providers and methods are the server's list.** `unknown_provider` and `unknown_method` come
 * back naming what is accepted, which is more current than any list this screen could carry.
 *
 * No design shows this; built from 08-payments/07 §6 per the 2026-10-26 ruling.
 */
import { useState } from "react";
import { Input, Select } from "../components/forms";
import { DataTable } from "../components/data";
import { EmptyState, ErrorState, LoadingState } from "../components/feedback";
import { ConfirmAction, Money, ScopeAction, Section } from "../components/kit";
import { useConsole } from "../app/console";
import { errorMessage } from "../lib/api/client";
import { newIdempotencyKey } from "../lib/api/idempotency";
import {
  useAddGatewayFee,
  useGatewayFees,
  type GatewayFeeIn,
} from "../lib/api/hooks/recon";

const KINDS: readonly GatewayFeeIn["fee_kind"][] = [
  "payment",
  "transfer",
  "payout",
  "refund",
  "chargeback",
];

/**
 * A percentage as typed ("2", "1.99") to the fraction the server stores ("0.0200", "0.0199").
 * Through whole basis points, so 1.99 never arrives as 0.019899999. Null for anything else.
 */
export function toRate(percent: string): string | null {
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(percent.trim())) return null;
  return (Math.round(Number(percent) * 100) / 10000).toFixed(4);
}

/** The stored fraction back as a percentage, before GST — the way the quote is written. */
export function asPercent(rate: string): string {
  return `${(Number(rate) * 100).toFixed(2)}%`;
}

const BLANK = {
  provider: "",
  fee_kind: "payment",
  method: "any",
  percent: "",
  fixed: "0",
  from: "",
  source: "",
};

export function GatewayFees() {
  const { scope, toast } = useConsole();
  const fees = useGatewayFees();
  const add = useAddGatewayFee();
  const [form, setForm] = useState(BLANK);
  /* Minted when the dialog opens, so a double-click or a retry replays the first answer. */
  const [key, setKey] = useState<string | null>(null);
  const set = (field: keyof typeof BLANK) => (value: string) =>
    setForm({ ...form, [field]: value });

  const rate = toRate(form.percent);
  const fixed = /^\d+$/.test(form.fixed.trim())
    ? Number(form.fixed.trim())
    : null;
  const ready =
    form.provider.trim() !== "" &&
    form.method.trim() !== "" &&
    rate !== null &&
    fixed !== null &&
    form.from !== "" &&
    form.source.trim().length >= 8;

  return (
    <div role="region" aria-label="Expected gateway fees">
      <Section
        title="Expected gateway fees"
        note="The written quote reconciliation checks each provider against. Nothing is booked from it."
      >
        {fees.isPending && <LoadingState message="Reading the fee schedule…" />}
        {fees.isError && (
          <ErrorState
            title="The fee schedule could not be read"
            cause={errorMessage(fees.error)}
          />
        )}
        {fees.isSuccess && fees.data.items.length === 0 && (
          <EmptyState
            title="No fee line yet"
            body="Until one is added, a provider's deduction has nothing to be checked against."
            inline
          />
        )}
        {fees.isSuccess && fees.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: "provider", label: "Provider" },
              { key: "kind", label: "For" },
              { key: "method", label: "Method", mono: true },
              { key: "rate", label: "Rate", align: "right", mono: true },
              { key: "fixed", label: "Fixed", align: "right" },
              { key: "window", label: "In force" },
              { key: "source", label: "Quote" },
            ]}
            rows={fees.data.items.map((f) => ({
              id: f.id,
              provider: f.provider,
              kind: f.fee_kind,
              method: f.method,
              rate: asPercent(f.rate),
              fixed: <Money paise={f.fixed_paise} showPaise />,
              window: `${f.effective_from} → ${f.effective_to ?? "open"}`,
              source: f.source_note,
            }))}
          />
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--sp-sm)",
            alignItems: "flex-end",
            marginTop: "var(--sp-md)",
            flexWrap: "wrap",
          }}
        >
          <Input
            label="Provider"
            value={form.provider}
            onChange={set("provider")}
            placeholder="razorpay"
            density="admin"
          />
          <Select
            label="For"
            value={form.fee_kind}
            options={KINDS}
            onChange={set("fee_kind")}
          />
          <Input
            label="Method"
            value={form.method}
            onChange={set("method")}
            density="admin"
          />
          <Input
            label="Rate (%) before GST"
            value={form.percent}
            onChange={set("percent")}
            placeholder="2"
            density="admin"
          />
          <Input
            label="Fixed fee (paise)"
            value={form.fixed}
            onChange={set("fixed")}
            density="admin"
          />
          <Input
            label="In force from"
            type="date"
            value={form.from}
            onChange={set("from")}
            density="admin"
          />
          <Input
            label="Quote (date and sender)"
            value={form.source}
            onChange={set("source")}
            placeholder="Razorpay quote, 2026-09-12, account manager"
            density="admin"
          />
          <ScopeAction
            scope={scope}
            need="finance"
            size="sm"
            disabled={!ready}
            onClick={() => setKey(newIdempotencyKey())}
          >
            Add a fee line
          </ScopeAction>
        </div>

        <ConfirmAction
          open={key !== null}
          title="Add this fee line?"
          phrase="ADD"
          tone="warn"
          reason
          consequences={[
            `${form.provider.trim()} · ${form.fee_kind} · ${form.method.trim()}: ${form.percent.trim()}% plus ${form.fixed.trim()} paise, from ${form.from}.`,
            "If a line for the same schedule is still open, it closes the day before this one starts.",
            "Reconciliation checks deductions against it from then on. Nothing is booked from it.",
          ]}
          onCancel={() => setKey(null)}
          onConfirm={(reason) =>
            add.mutate(
              {
                key: key!,
                provider: form.provider.trim(),
                fee_kind: form.fee_kind as GatewayFeeIn["fee_kind"],
                method: form.method.trim(),
                rate: rate!,
                fixed_paise: fixed!,
                effective_from: form.from,
                source_note: form.source.trim(),
                reason,
              },
              {
                onSuccess: () => {
                  toast({
                    tone: "success",
                    message: `Fee line added for ${form.provider.trim()}`,
                  });
                  setKey(null);
                  setForm(BLANK);
                },
                onError: (error) =>
                  toast({ tone: "error", message: errorMessage(error) }),
              },
            )
          }
          confirmLabel="Add"
        />
      </Section>
    </div>
  );
}
